/**
 * Dynamic polyfill loader
 * Loads polyfills only when needed (ad-hoc loading)
 */

export interface PolyfillConfig {
  webcomponents?: boolean;
  shadowDom?: boolean;
  customElements?: boolean;
  es2020?: boolean;
}

interface FeatureDetection {
  webcomponents: boolean;
  shadowDom: boolean;
  customElements: boolean;
  es2020: boolean;
  fetch: boolean;
  promises: boolean;
}

/**
 * Detect browser features
 */
function detectFeatures(): FeatureDetection {
  return {
    webcomponents: 
      typeof window !== 'undefined' &&
      'attachShadow' in Element.prototype &&
      'getRootNode' in Element.prototype,
    
    shadowDom: 
      typeof window !== 'undefined' &&
      !!HTMLElement.prototype.attachShadow,
    
    customElements: 
      typeof window !== 'undefined' &&
      'customElements' in window,
    
    es2020: (() => {
      try {
        // Test optional chaining
        new Function('const obj = {}; const test = obj?.prop;')();
        // Test nullish coalescing
        new Function('const value = null ?? "default";')();
        return true;
      } catch {
        return false;
      }
    })(),
    
    fetch: typeof fetch !== 'undefined',
    promises: typeof Promise !== 'undefined'
  };
}

/**
 * Load script from URL
 */
function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Load required polyfills based on feature detection
 */
export async function loadPolyfills(config: PolyfillConfig = {}): Promise<void> {
  const features = detectFeatures();
  const polyfills: Promise<void>[] = [];

  // Load Web Components polyfill (includes Custom Elements and Shadow DOM)
  if ((config.webcomponents || config.shadowDom || config.customElements) && 
      (!features.webcomponents || !features.customElements)) {
    polyfills.push(
      loadScript('https://unpkg.com/@webcomponents/webcomponentsjs@2.8.0/webcomponents-bundle.js')
    );
  }

  // Load fetch polyfill
  if (!features.fetch) {
    polyfills.push(
      loadScript('https://unpkg.com/unfetch@5.0.0/polyfill.js')
    );
  }

  // Wait for all polyfills to load
  if (polyfills.length > 0) {
    await Promise.all(polyfills);
  }
}

/**
 * Check if polyfills are needed
 */
export function needsPolyfills(config: PolyfillConfig = {}): boolean {
  const features = detectFeatures();

  if (config.webcomponents && !features.webcomponents) return true;
  if (config.shadowDom && !features.shadowDom) return true;
  if (config.customElements && !features.customElements) return true;
  if (config.es2020 && !features.es2020) return true;

  return false;
}

/**
 * Initialize polyfills automatically if needed
 */
export function initializePolyfills(): void {
  if (typeof window !== 'undefined' && needsPolyfills({
    webcomponents: true,
    es2020: true
  })) {
    loadPolyfills({
      webcomponents: true,
      es2020: true
    }).catch(console.error);
  }
}

// Auto-initialize if in browser
if (typeof window !== 'undefined') {
  // Wait for DOM to be ready, then load polyfills if needed
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePolyfills);
  } else {
    initializePolyfills();
  }
}

export { needsPolyfills as detectPolyfills } from './detect';
